import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Building2,
  Car,
  FileCheck,
  Settings,
  LogOut,
  User,
  CheckCircle,
  XCircle,
  Clock,
  BarChart3,
  Calendar,
  CreditCard,
  MessageSquare,
  ChevronDown,
  ChevronRight,
  DollarSign,
  Plus,
  List,
  BookOpen,
  UserCheck
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { ROUTES } from '../../constants/routes.constants';
import { PERMISSIONS } from '../../utils/rbac';
import Logo from '../common/Logo';

/**
 * Sidebar centralisée avec sections masquées selon le rôle / permissions
 * Design moderne et professionnel
 */
const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasPermission } = useAuth();
  const { t } = useLanguage();

  // État pour gérer les menus déroulants
  const [expandedMenus, setExpandedMenus] = React.useState(() => {
    // Initialiser avec les menus ouverts en fonction de la route actuelle
    return {
      vehicles:
        location.pathname.startsWith('/admin/vehicles') ||
        location.pathname.startsWith('/vendor/vehicles'),
      bookings: location.pathname.startsWith('/vendor/bookings'),
    };
  });

  // Menu items par rôle avec traductions
  const menuItems = {
    admin: [
      {
        id: 'dashboard',
        label: t('sidebar.admin.dashboard'),
        icon: LayoutDashboard,
        path: '/admin/dashboard',
        badge: null,
      },
      {
        id: 'pending-agencies',
        label: t('sidebar.admin.pendingAgencies'),
        icon: Clock,
        path: '/admin/pending-agencies',
        badge: 'pending',
        permission: PERMISSIONS.AGENCIES_APPROVE,
      },
      {
        id: 'agencies',
        label: t('sidebar.admin.agencies'),
        icon: Building2,
        path: '/admin/agencies',
        permission: PERMISSIONS.AGENCIES_APPROVE,
      },
      {
        id: 'users',
        label: t('sidebar.admin.users'),
        icon: Users,
        path: '/admin/users',
        permission: PERMISSIONS.USERS_READ,
      },
      {
        id: 'bookings',
        label: t('sidebar.admin.bookings'),
        icon: BookOpen,
        path: '/admin/bookings',
        permission: PERMISSIONS.BOOKINGS_READ,
      },
      {
        id: 'audit-logs',
        label: t('sidebar.admin.auditLogs'),
        icon: FileCheck,
        path: '/admin/audit-logs',
        permission: PERMISSIONS.AUDIT_READ,
      },
      {
        id: 'vehicles',
        label: t('sidebar.admin.vehicles'),
        icon: Car,
        path: '/admin/vehicles',
        permission: PERMISSIONS.VEHICLES_READ,
        submenu: [
          {
            id: 'vehicle-list',
            label: t('sidebar.admin.allVehicles'),
            icon: List,
            path: ROUTES.ADMIN_VEHICLES_LIST,
            isActivePath: (pathname) =>
              pathname.startsWith('/admin/vehicles/list') ||
              pathname.startsWith('/admin/vehicles/review') ||
              pathname.startsWith('/admin/vehicles/edit') ||
              pathname.startsWith('/admin/vehicles/add'),
          },
          {
            id: 'vehicle-settings',
            label: t('sidebar.admin.vehicleSettings'),
            icon: Settings,
            path: ROUTES.ADMIN_VEHICLES_SETTINGS,
          },
        ],
      },
      {
        id: 'analytics',
        label: t('sidebar.admin.analytics'),
        icon: BarChart3,
        path: '/admin/analytics',
      },
      {
        id: 'settings',
        label: t('sidebar.admin.settings'),
        icon: Settings,
        path: '/admin/settings',
        permission: PERMISSIONS.SETTINGS_WRITE,
      },
    ],
    super_admin: null, // alias → admin
    staff: null, // alias → admin (filtré par permissions)
    vendor: [
      {
        id: 'dashboard',
        label: t('sidebar.vendor.dashboard'),
        icon: LayoutDashboard,
        path: '/vendor/dashboard',
      },
      {
        id: 'vehicles',
        label: t('sidebar.vendor.vehicles'),
        icon: Car,
        path: '/vendor/vehicles',
        submenu: [
          {
            id: 'vendor-vehicles-list',
            label: t('sidebar.vendor.vehicleList'),
            icon: List,
            path: '/vendor/vehicles/list',
          },
          {
            id: 'vendor-vehicles-add',
            label: t('sidebar.vendor.addVehicle'),
            icon: Plus,
            path: '/vendor/vehicles/add',
          },
        ],
      },
      {
        id: 'bookings',
        label: t('sidebar.vendor.bookings'),
        icon: Calendar,
        path: '/vendor/bookings',
        submenu: [
          {
            id: 'vendor-bookings-all',
            label: 'Toutes les réservations',
            icon: List,
            path: '/vendor/bookings',
          },
          {
            id: 'vendor-bookings-pending',
            label: 'En attente',
            icon: Clock,
            path: '/vendor/bookings/pending',
          },
          {
            id: 'vendor-bookings-confirmed',
            label: 'Confirmées',
            icon: CheckCircle,
            path: '/vendor/bookings/confirmed',
          },
          {
            id: 'vendor-bookings-live',
            label: 'En cours',
            icon: Clock,
            path: '/vendor/bookings/live',
          },
          {
            id: 'vendor-bookings-completed',
            label: 'Terminées',
            icon: CheckCircle,
            path: '/vendor/bookings/completed',
          },
          {
            id: 'vendor-bookings-cancelled',
            label: 'Annulées',
            icon: XCircle,
            path: '/vendor/bookings/cancelled',
          },
          {
            id: 'vendor-bookings-declined',
            label: 'Refusées',
            icon: XCircle,
            path: '/vendor/bookings/declined',
          },
          {
            id: 'vendor-bookings-expired',
            label: 'Expirées',
            icon: Clock,
            path: '/vendor/bookings/expired',
          },
          {
            id: 'vendor-bookings-refunded',
            label: 'Remboursées',
            icon: DollarSign,
            path: '/vendor/bookings/refunded',
          },
        ],
      },
      {
        id: 'earnings',
        label: t('sidebar.vendor.earnings'),
        icon: CreditCard,
        path: '/vendor/earnings',
      },
      {
        id: 'messages',
        label: t('sidebar.vendor.messages'),
        icon: MessageSquare,
        path: '/vendor/messages',
      },
      {
        id: 'settings',
        label: t('sidebar.vendor.settings'),
        icon: Settings,
        path: '/vendor/settings',
      },
    ],
    user: [
      {
        id: 'dashboard',
        label: t('sidebar.user.dashboard'),
        icon: LayoutDashboard,
        path: '/user/dashboard',
      },
      {
        id: 'bookings',
        label: t('sidebar.user.bookings'),
        icon: Calendar,
        path: '/user/bookings',
      },
      {
        id: 'favorites',
        label: t('sidebar.user.favorites'),
        icon: Car,
        path: '/user/favorites',
      },
      {
        id: 'payments',
        label: t('sidebar.user.payments'),
        icon: CreditCard,
        path: '/user/payments',
      },
      {
        id: 'profile',
        label: t('sidebar.user.profile'),
        icon: User,
        path: '/user/profile',
      },
      {
        id: 'settings',
        label: t('sidebar.user.settings'),
        icon: Settings,
        path: '/user/settings',
      },
    ],
  };

  // Obtenir les items du menu selon le rôle + permissions
  const getMenuItems = () => {
    if (!user?.role) return [];
    const roleKey =
      user.role === 'super_admin' || user.role === 'staff' ? 'admin' : user.role;
    const items = menuItems[roleKey] || menuItems.user;
    return items.filter((item) => {
      if (!item.permission) return true;
      return hasPermission(item.permission);
    });
  };

  const currentItems = getMenuItems();
  const isActive = (path, isActivePath) => {
    if (typeof isActivePath === 'function') {
      return isActivePath(location.pathname);
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const handleLogout = async () => {
    await logout();
    navigate(ROUTES.HOME);
  };

  // Statut de l'agence (si vendor)
  const getVendorStatus = () => {
    if (user?.role !== 'vendor') return null;
    // TODO: Récupérer le statut réel depuis l'API
    const status = user.vendorStatus || 'PENDING'; // PENDING, APPROVED, REJECTED

    const statusConfig = {
      PENDING: {
        icon: Clock,
        color: 'text-yellow-600',
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        label: t('sidebar.vendor.status.pending'),
      },
      APPROVED: {
        icon: CheckCircle,
        color: 'text-green-600',
        bg: 'bg-green-50',
        border: 'border-green-200',
        label: t('sidebar.vendor.status.approved'),
      },
      REJECTED: {
        icon: XCircle,
        color: 'text-red-600',
        bg: 'bg-red-50',
        border: 'border-red-200',
        label: t('sidebar.vendor.status.rejected'),
      },
    };

    return statusConfig[status] || statusConfig.PENDING;
  };

  const vendorStatus = getVendorStatus();

  return (
    <div className="fixed top-16 left-0 h-[calc(100vh-4rem)] w-64 bg-white border-r border-gray-200 flex flex-col shadow-sm z-40">
      {/* Vendor Status Banner (si vendor) */}
      {vendorStatus && (
        <div className={`mx-4 mt-4 p-3 rounded-lg border ${vendorStatus.bg} ${vendorStatus.border}`}>
          <div className="flex items-center gap-2">
            <vendorStatus.icon className={`w-4 h-4 ${vendorStatus.color}`} />
            <span className={`text-xs font-medium ${vendorStatus.color}`}>
              {vendorStatus.label}
            </span>
          </div>
        </div>
      )}

      {/* Navigation Menu */}
      <nav className="flex-1 overflow-y-auto py-2 px-2">
        <div className="space-y-0.5">
          {currentItems.map((item) => {
            const Icon = item.icon;
            const hasSubmenu = item.submenu && item.submenu.length > 0;
            const isExpanded = expandedMenus[item.id];
            const active = isActive(item.path) || (hasSubmenu && item.submenu.some((sub) => isActive(sub.path, sub.isActivePath)));

            // Menu avec sous-menu
            if (hasSubmenu) {
              return (
                <div key={item.id}>
                  <button
                    onClick={() => setExpandedMenus(prev => ({ ...prev, [item.id]: !prev[item.id] }))}
                    className={`
                      w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-sm leading-5 font-medium transition-all
                      ${active
                        ? 'bg-gray-100 text-[#27489E]'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-[#27489E]'
                      }
                    `}
                  >
                    <Icon className={`w-4 h-4 ${active ? 'text-[#27489E]' : 'text-gray-500'}`} />
                    <span className="flex-1 text-left">{item.label}</span>
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                  </button>

                  {/* Sous-menu */}
                  {isExpanded && (
                    <div className="ml-3 mt-0.5 space-y-0.5 border-l border-gray-200 pl-3">
                      {item.submenu.map((subItem) => {
                        const SubIcon = subItem.icon;
                        const subActive = isActive(subItem.path, subItem.isActivePath);

                        return (
                          <Link
                            key={subItem.id}
                            to={subItem.path}
                            className={`
                              flex items-center gap-2 px-2 py-1 rounded-md text-sm leading-5 font-medium transition-all
                              ${subActive
                                ? 'bg-[#27489E] text-white shadow-sm'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-[#27489E]'
                              }
                            `}
                          >
                            <SubIcon className={`w-3.5 h-3.5 ${subActive ? 'text-white' : 'text-gray-400'}`} />
                            <span className="flex-1">{subItem.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            // Menu simple sans sous-menu
            return (
              <Link
                key={item.id}
                to={item.path}
                className={`
                  flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-sm leading-5 font-medium transition-all
                  ${active
                    ? 'bg-[#27489E] text-white shadow-sm'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-[#27489E]'
                  }
                `}
              >
                <Icon className={`w-4 h-4 ${active ? 'text-white' : 'text-gray-500'}`} />
                <span className="flex-1">{item.label}</span>
                {item.badge && (
                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-700">
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </nav>

    </div>
  );
};

export default Sidebar;

